import * as React from "react";
import { useSheetSettled } from "@/hooks/use-sheet-settled";
import {
  SHEET_FULL_ON_MOBILE_CLASS,
  SHEET_GROW_TO_FULL_CLASS,
  SHEET_PAGE_MIN_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";

/** El mismo píxel que `max-md` de Tailwind y que `useIsPhone`. */
const PHONE_QUERY = "(max-width: 767px)";

/**
 * Qué elemento se mide.
 *
 * Las carcasas que montan su propio cuerpo (FormSheet, GlassSheet, el conmutador
 * de voz) pasan directamente el que hace scroll. Las que solo ponen el marco y
 * dejan el interior al consumidor (el cajón, la hoja lateral) pasan el PANEL: allí
 * el que hace scroll es un hijo del consumidor, y se le encuentra por
 * `data-sheet-scroll`. Sin esa marca se mide el panel, que es lo correcto cuando
 * el propio panel es el que se desplaza.
 */
function resolveScroller(host: HTMLElement | null): HTMLElement | null {
  if (!host) return null;
  return (
    host.querySelector<HTMLElement>("[data-sheet-scroll]") ??
    // `ScrollArea` de Radix esconde el que se desplaza en un hijo suyo.
    host.querySelector<HTMLElement>("[data-radix-scroll-area-viewport]") ??
    host
  );
}

/**
 * Decide si una hoja inferior tiene que estirarse a pantalla completa.
 *
 * El alto deja de escribirse a mano en cada pantalla y pasa a MEDIRSE. La razón es
 * que ninguna constante puede acertar: «Configuración de líneos» es un campo por
 * sector, así que con dos sectores tiene que ser corta y con doce tiene que ocupar
 * la pantalla. Eso solo lo sabe el contenido, en tiempo de ejecución.
 *
 * - `auto` (lo normal): corta si cabe, a pantalla completa si se desborda.
 * - `expanded`: siempre a pantalla completa. Para asistentes de varios pasos, donde
 *   lo que importa no es la longitud sino que NO cambie de tamaño entre pasos —el
 *   paso 1 son dos campos y el 3 una tabla, y una hoja que encoge y crece parece
 *   que se está reabriendo.
 * - `short`: nunca. Para las que tapar la pantalla les quita el sentido: la ficha
 *   del sensor sobre el mapa, los filtros, un menú de tres líneas.
 *
 * Tres detalles que son el 90% de los fallos de algo así:
 *
 * 1. `useLayoutEffect`: se mide ANTES de pintar. Con `useEffect` se ve un
 *    fotograma con la hoja corta y luego el estirón.
 * 2. Se ENGANCHA: una vez estirada, no vuelve a encogerse mientras esté abierta.
 *    Sin esto, una consulta que termina y quita un «cargando…» encoge la hoja
 *    debajo del dedo. Se suelta al cerrar y al cambiar de paso (`resetKey`).
 * 3. Solo en el teléfono. De `md` en adelante esto no es una hoja, es una ventana
 *    centrada, y estirarla no significa nada.
 */
export function useSheetFit({
  fit = "auto",
  host,
  resetKey,
}: {
  fit?: SheetFit;
  /**
   * El panel ya montado, o `null` si la hoja está cerrada. Un NODO, no una ref, y
   * esa es toda la diferencia entre que esto funcione o no.
   *
   * Iba con `React.useRef` + una prop `open`, y así solo medía la única carcasa
   * que se acordaba de pasar `open`. En las demás, el efecto se ejecutaba una vez
   * al montarse el componente —con la hoja todavía CERRADA, o sea con la ref a
   * null—, salía por la puerta de atrás y no volvía a mirar nunca: Radix monta el
   * contenido en un portal al abrir, y eso no cambia ninguna dependencia. La hoja
   * se quedaba clavada en su alto mínimo sin que nada fallara.
   *
   * Con el nodo en un `useState`, montarse ES el cambio de dependencia.
   */
  host: HTMLElement | null;
  /** Cambia por paso en los asistentes: vuelve a medir y suelta el enganche. */
  resetKey?: string | number;
}): boolean {
  const [overflows, setOverflows] = React.useState(false);
  /* LOS OBSERVADORES NO SE MONTAN HASTA QUE LA HOJA SE HA POSADO (29-08-2026).
     Medir cuesta: un ResizeObserver sobre el cuerpo Y CADA HIJO, un
     MutationObserver de todo el subárbol, y un `scrollHeight` que fuerza
     reflujo síncrono. Todo eso arrancaba en un `useLayoutEffect` en el MISMO
     fotograma en que la hoja empieza a subir — o sea, el trabajo más pesado del
     ciclo justo encima de la animación. `FormSheet`, que es la carcasa de unos
     cuarenta formularios, entra por aquí sin pedirlo: su `fit` por defecto es
     `page`, y `page` mide igual que `auto`.
     La PRIMERA medida sí se hace en el primer fotograma —es una sola lectura de
     `scrollHeight`— para que la hoja abra directamente a su alto bueno. Lo que
     espera es la vigilancia continua. */
  const settled = useSheetSettled(!!host);
  const [isPhone, setIsPhone] = React.useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(PHONE_QUERY).matches
      : false,
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mql.matches);
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Al cerrar (el nodo se va), o al cambiar de paso, se suelta el enganche.
  React.useEffect(() => {
    if (!host) setOverflows(false);
  }, [host]);
  React.useEffect(() => {
    setOverflows(false);
  }, [resetKey]);

  React.useLayoutEffect(() => {
    // `page` mide igual que `auto`; lo único que cambia es que además tiene suelo.
    if ((fit !== "auto" && fit !== "page") || !isPhone || !host) return;

    // Se resuelve EN CADA MEDIDA, no una sola vez al montar. Hay hojas cuyo
    // cuerpo llega TARDE: la ficha de tarea del trabajador pinta primero un
    // esqueleto, que no lleva `data-sheet-scroll`, y hasta que no se posa no
    // existe el elemento que se desplaza. Quedándose con el de la primera
    // pasada se medía el PANEL, que está limitado por `max-h` y por tanto nunca
    // se desborda —sus hijos se encogen dentro—, así que la hoja no crecía
    // jamás por mucho que se le metiera dentro: se quedaba clavada en el tope
    // de 72dvh y con `auto` no había forma de llegar a pantalla completa.
    //
    // PERO solo puede relevar al de la primera pasada un `data-sheet-scroll`,
    // que es el contrato EXPLÍCITO. El `[data-radix-scroll-area-viewport]` de
    // `resolveScroller` es una heurística que solo era inofensiva porque se
    // miraba una vez, sobre el árbol que la carcasa acababa de montar; volver a
    // mirarla en cada mutación la convierte en «manda para siempre el primer
    // ScrollArea que aparezca», y eso es falso: en «Importar inventario» el
    // ScrollArea del paso 2 mide 400 px fijos y en el ciclo de cultivo la lista
    // de naves mide 128 px — se desbordan por construcción, no porque la hoja
    // necesite crecer, así que ambas se estirarían a pantalla completa y, como
    // el enganche solo va hacia arriba, se quedarían así.
    const initial = resolveScroller(host);
    const current = () =>
      host.querySelector<HTMLElement>("[data-sheet-scroll]") ?? initial;

    // Un pelo de margen: 4px de diferencia no es «se desborda», es redondeo.
    const measure = () => {
      const el = current();
      if (!el) return;
      if (el.scrollHeight > el.clientHeight + 4) setOverflows(true); // engancha
    };
    /* LA PRIMERA MEDIDA, YA. Es UNA lectura de `scrollHeight`: comparada con lo
       que viene debajo no cuesta nada, y hacerla en el primer fotograma es lo
       que hace que la hoja abra DIRECTAMENTE a su alto bueno.
       Retrasarla también —como se hizo el 29-08— salía peor por el otro lado:
       la hoja abría a 72dvh y CRECÍA sola tres décimas después, ya posada, que
       es el «se abre y luego se redimensiona» que se veía en «Tratamientos» y en
       «Añadir nueva plaga». Un estirón durante la subida se disimula; el mismo
       estirón con la hoja quieta se ve entero. */
    measure();

    /* Y LOS OBSERVADORES, DESPUÉS DE POSARSE. Esto es lo caro: un
       ResizeObserver sobre el cuerpo Y CADA HIJO, un MutationObserver de todo
       el subárbol, y su callback volviendo a leer `scrollHeight` —reflujo
       síncrono— cada vez que algo cambia ahí dentro. Montado en el primer
       fotograma, todo eso compite con la animación. Montado al final, no. */
    if (!settled) return;

    if (typeof ResizeObserver === "undefined") return;
    // El contenido llega tarde (una consulta, una imagen): hay que volver a mirar.
    const ro = new ResizeObserver(measure);
    const watch = () => {
      ro.disconnect();
      const next = current();
      if (!next) return;
      ro.observe(next);
      for (const child of Array.from(next.children)) ro.observe(child);
    };
    watch();

    if (typeof MutationObserver === "undefined" || typeof requestAnimationFrame === "undefined") {
      return () => ro.disconnect();
    }
    // Cuando el cuerpo real sustituye al esqueleto no cambia NINGUNA dependencia
    // de este efecto (el panel es el mismo nodo) y los hijos que vigilaba el
    // ResizeObserver ya no están en la página: hay que volver a mirar y volver a
    // engancharse. Se agrupa en un fotograma porque montar un árbol grande
    // dispara muchas mutaciones seguidas.
    let queued = 0;
    const mo = new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        watch();
        measure();
      });
    });
    mo.observe(host, { childList: true, subtree: true });

    return () => {
      if (queued) cancelAnimationFrame(queued);
      mo.disconnect();
      ro.disconnect();
    };
  }, [host, fit, isPhone, resetKey, settled]);

  if (!isPhone) return false;
  if (fit === "expanded") return true;
  if (fit === "short") return false;
  return overflows;
}

/**
 * Lo mismo que `useSheetFit`, pero devolviendo YA la clase de alto que toca.
 *
 * Es lo que usan las carcasas: así el «cómo se dice en Tailwind» vive en un solo
 * sitio y ninguna vuelve a escribir un `dvh` a mano.
 *
 * Devuelve `undefined` cuando no hay nada que decir (escritorio, una hoja `short`,
 * o una `auto` cuyo contenido cabe): así la clase del consumidor se queda intacta
 * y este sistema no cambia nada de lo que ya funcionaba. Ver `SheetFit` para por
 * qué `short` no tiene clase propia.
 *
 * OJO al orden: la carcasa tiene que ponerla DESPUÉS del `className` del
 * consumidor. Las dos son del mismo grupo para tailwind-merge, así que gana la
 * última — y si se pusiera antes, un `max-md:!h-[62dvh]` de una pantalla
 * concreta se comería la decisión de la carcasa sin que nada fallara.
 */
export function useSheetFitClass(opts: {
  fit?: SheetFit;
  host: HTMLElement | null;
  resetKey?: string | number;
}): string | undefined {
  const grown = useSheetFit(opts);
  /* `expanded` fija el alto (no debe moverse entre pasos); `auto` y `page` solo
     suben el techo, para crecer lo justo y no dejar un hueco. `page` añade
     además su suelo, que es lo que hace que abra alta aunque lleve poco. */
  if (opts.fit === "expanded") return grown ? SHEET_FULL_ON_MOBILE_CLASS : undefined;
  if (opts.fit === "page") {
    return grown ? `${SHEET_PAGE_MIN_CLASS} ${SHEET_GROW_TO_FULL_CLASS}` : SHEET_PAGE_MIN_CLASS;
  }
  return grown ? SHEET_GROW_TO_FULL_CLASS : undefined;
}
